import { responseRejectInterceptor } from "@api/base";
import { documentRequestApi } from "@api/documentRequests";
import { NewButton } from "@components/backoffice/buttons/NewButton";
import { ExportReactSearchKitResults } from "@components/backoffice/ExportSearchResults";
import { QueryBuildHelper } from "@components/SearchBar/QueryBuildHelper";
import {
  invenioConfig,
  setReactSearchKitDefaultSortingOnEmptyQueryString,
  setReactSearchKitInitialQueryState,
  setReactSearchKitUrlHandler,
} from "@config";
import history from "@history";
import SearchAggregationsCards from "@modules/SearchControls/SearchAggregationsCards";
import { SearchControls } from "@modules/SearchControls/SearchControls";
import { SearchControlsOverridesMap } from "@modules/SearchControls/SearchControlsOverrides";
import SearchFooter from "@modules/SearchControls/SearchFooter";
import ClearFiltersButton from "@modules/SearchControls/ClearFiltersButton";
import SessionFiltersPersistence from "@modules/SearchControls/SessionFiltersPersistence";
import { BackOfficeRoutes } from "@routes/urls";
import React, { Component } from "react";
import { OverridableContext } from "react-overridable";
import {
  EmptyResults,
  Error,
  InvenioSearchApi,
  ReactSearchKit,
  ResultsList,
  ResultsLoader,
  SearchBar,
} from "react-searchkit";
import { Container, Grid, Header } from "semantic-ui-react";
import { DocumentRequestListEntry } from "./DocumentRequestListEntry";
import Qs from "qs";
import axios from "axios";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";

export class DocumentRequestSearch extends Component {
  modelName = "DOCUMENT_REQUESTS";

  searchApi = new InvenioSearchApi({
    axios: {
      url: documentRequestApi.searchBaseURL,
      withCredentials: true,
    },
    interceptors: {
      response: { reject: responseRejectInterceptor },
    },
  });

  getRequestSerializer = () => {
    const requestSerializer = this.searchApi.requestSerializer;
    if (
      !requestSerializer ||
      typeof requestSerializer.serialize !== "function"
    ) {
      throw new Error(
        "RequestSerializer is not configured correctly in DocumentRequestSearch."
      );
    }
    return requestSerializer;
  };

  handleExportClick = async (format, size, currentQueryState) => {
    switch (format) {
      case "pdf":
        return this.exportToPDF(currentQueryState, size);
      case "csv":
        return this.handleStandardExport(currentQueryState, size, format);
      default:
        return this.handleStandardExport(currentQueryState, size, "csv");
    }
  };

  handleStandardExport = async (currentQueryState, size, format) => {
    const requestSerializer = this.getRequestSerializer();
    const queryString = requestSerializer.serialize({
      ...currentQueryState,
      size,
    });
    const params = Qs.parse(queryString);
    params["page"] = 1;
    params["format"] = format;

    const exportUrl = `${documentRequestApi.searchBaseURL}?${Qs.stringify(
      params,
      {
        arrayFormat: "repeat",
        encode: false,
      }
    )}`;

    window.open(exportUrl, "_blank");
  };

  exportToPDF = async (currentQueryState, size = 1000) => {
    try {
      const requestSerializer = this.getRequestSerializer();
      const queryString = requestSerializer.serialize({
        ...currentQueryState,
        size,
      });
      const params = Qs.parse(queryString);
      params.page = 1;
      params.size = size;
      const exportUrl = `${documentRequestApi.searchBaseURL}?${Qs.stringify(
        params,
        {
          arrayFormat: "repeat",
          encode: false,
        }
      )}`;

      const { data } = await axios.get(exportUrl, { withCredentials: true });
      const hits = data.hits?.hits || [];

      const tableRows = hits
        .map((hit, idx) => {
          const metadata = hit.metadata || {};
          const title = metadata.title || "";
          const authors = metadata.authors
            ? Array.isArray(metadata.authors)
              ? metadata.authors.map((a) => a.full_name || a).join("; ")
              : metadata.authors
            : "";
          const publicationYear = metadata.publication_year || "";
          const materialType = metadata.material_type || "";
          return `
        <tr>
          <td>${idx + 1}</td>
          <td>${title}</td>
          <td>${authors}</td>
          <td>${publicationYear}</td>
          <td>${materialType}</td>
        </tr>
      `;
        })
        .join("");

      const tableHeaders = `
      <th>#</th>
      <th>Title</th>
      <th>Author(s)</th>
      <th>Publication Year</th>
      <th>Material Type</th>
    `;

      const htmlContent = generateExportHTML({
        reportTitle: "Document Requests Export Report",
        generatedDate: new Date().toLocaleString(),
        tableHeaders,
        tableRows,
        showUniversityLogo: true,
        showLrcLogo: true,
        fallbackLogoText: "Library Resource Center",
      });

      const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      if (printWindow) {
        printWindow.onload = () => setTimeout(() => printWindow.focus(), 500);
      }
    } catch (error) {
      alert("Failed to export PDF. Please try again.");
    }
  };

  render() {
    const helperFields = [
      {
        name: "document PID",
        field: "document_pid",
        defaultValue: "1",
      },
      {
        name: "title",
        field: "title",
        defaultValue: "Harry Potter",
      },
    ];

    const initialState = setReactSearchKitInitialQueryState(this.modelName);
    const defaultSortingOnEmptyQueryString =
      setReactSearchKitDefaultSortingOnEmptyQueryString(this.modelName);
    const urlHandler = setReactSearchKitUrlHandler(this.modelName);

    return (
      <>
        <Header as="h2">Requests for New Book</Header>
        <OverridableContext.Provider
          value={{
            ...SearchControlsOverridesMap,
          }}
        >
          <ReactSearchKit
            searchApi={this.searchApi}
            history={history}
            urlHandlerApi={urlHandler}
            initialQueryState={initialState}
            defaultSortingOnEmptyQueryString={defaultSortingOnEmptyQueryString}
          >
            <>
              <SessionFiltersPersistence moduleIdentifier="document-requests" />
              <Container fluid className="spaced">
                <SearchBar
                  placeholder="Search for document requests"
                  {...invenioConfig.APP.SEARCH_BAR_PROPS}
                />
                <QueryBuildHelper fields={helperFields} />
              </Container>
              <Container fluid className="bo-search-body">
                <Grid>
                  <Grid.Row columns={2}>
                    <ResultsLoader>
                      <Grid.Column width={3} className="search-aggregations">
                        <Grid>
                          <Grid.Row
                            verticalAlign="middle"
                            className="search-clear-filters"
                          >
                            <Grid.Column width={16}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "1rem",
                                }}
                              >
                                <Header
                                  as="h4"
                                  content="Filter by"
                                  style={{ margin: 0 }}
                                />
                                <ClearFiltersButton module-identifier="document-requests" />
                              </div>
                            </Grid.Column>
                          </Grid.Row>
                        </Grid>
                        <SearchAggregationsCards modelName={this.modelName} />
                      </Grid.Column>
                      <Grid.Column width={13}>
                        <Grid columns={2}>
                          <Grid.Column width={8} />
                          <Grid.Column width={8} textAlign="right">
                            <ExportReactSearchKitResults
                              exportBaseUrl={documentRequestApi.searchBaseURL}
                              onExportClick={this.handleExportClick}
                              supportedFormats={["csv", "pdf"]}
                            />
                          </Grid.Column>
                        </Grid>
                        <EmptyResults
                          extraContent={
                            <NewButton
                              text="Add request"
                              to={BackOfficeRoutes.documentRequestCreate}
                            />
                          }
                        />
                        <Error />
                        <SearchControls
                          modelName={this.modelName}
                          withLayoutSwitcher={false}
                        />
                        <ResultsList
                          ListEntryElement={DocumentRequestListEntry}
                        />
                        <SearchFooter />
                      </Grid.Column>
                    </ResultsLoader>
                  </Grid.Row>
                </Grid>
              </Container>
            </>
          </ReactSearchKit>
        </OverridableContext.Provider>
      </>
    );
  }
}
